import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import TOPIC_PHOTOS from "../lib/topicPhotos";
import Navbar from "../components/Navbar";
import { useLang } from "../contexts/LanguageContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  ArrowLeft, BookOpen, ChevronRight, Target, AlertTriangle, Sparkles,
  MapPin, ScrollText, ShieldCheck, ExternalLink,
} from "lucide-react";
import { RightOfWayScenariosPanel } from "../components/RightOfWayIllustrations";

/* ------------------------------------------------------------------ */
/* Curated visual map — per-lesson hero illustration (instead of      */
/* dumping every related sign, which can overwhelm new learners).     */
/* Caption is trilingual.                                             */
/* ------------------------------------------------------------------ */
const LESSON_VISUALS = {
  "sp-1": {
    src: "/illustrations/speed_limits.svg",
    caption: {
      en: "Default Norwegian speed limits — built-up areas, rural roads, motorway.",
      so: "Xadka xawaaraha caadiga ah ee Norway — magaalo, miyi iyo motorway.",
      ar: "الحدود الافتراضية للسرعة في النرويج — داخل المدن، الريف والطرق السريعة.",
    },
  },
  "sp-2": {
    src: "/illustrations/stopping_distance.svg",
    caption: {
      en: "Stopping distance = reaction distance + braking distance. Doubles on wet, triples on ice.",
      so: "Masaafada joojinta = masaafada falcelinta + masaafada bireegga. Way labanlaabantaa qoyaan, saddex jeer baraf.",
      ar: "مسافة التوقف = مسافة رد الفعل + مسافة الكبح. تتضاعف على الرطب، تتثلث على الجليد.",
    },
  },
  "sp-3": {
    src: "/illustrations/overtaking.svg",
    caption: {
      en: "Overtake only on a broken centre line, with full view and enough distance.",
      so: "Kor u maro kaliya markay khadka dhexe jaban yahay, aragga buuxa iyo masaafa ku filan.",
      ar: "لا تتجاوز إلا على الخط الأوسط المتقطع، برؤية كاملة ومسافة كافية.",
    },
  },
  "sp-4": {
    src: "/illustrations/road_grip.svg",
    caption: {
      en: "Grip changes the road. Wet doubles braking, ice can quadruple it.",
      so: "Qabashada wadada way isbeddeshaa. Qoyaan way labanlaabataa, baraf wuu afar jibaaraa.",
      ar: "تختلف قوة التماسك. على الرطب تتضاعف مسافة الكبح، وعلى الجليد تتضاعف أربع مرات.",
    },
  },
  "ru-1": {
    src: "/illustrations/vulnerable_road_users.svg",
    caption: {
      en: "Pedestrians, children, elderly, cyclists and motorcyclists need extra space and eye contact.",
      so: "Lugeyn, carruur, waayeello, baaskiilayaal iyo mooto-wadayaal waxay u baahan yihiin meel iyo isu-eegmo dheeraad ah.",
      ar: "المشاة، الأطفال، كبار السن، راكبو الدراجات والدراجات النارية يحتاجون مساحة إضافية وتواصلًا بصريًا.",
    },
  },
  "ru-2": {
    src: "/illustrations/reaction_time.svg",
    caption: {
      en: "Distractions multiply reaction time. Phones can quadruple your stopping distance.",
      so: "Hawl-gelinta way kordhisaa wakhtiga falcelinta. Telefoonnada waxay afar jibaari karaan masaafada joojinta.",
      ar: "تضاعف عوامل التشتيت زمن رد الفعل. الهاتف قد يربع مسافة توقفك.",
    },
  },
  "rs-1": {
    src: "/illustrations/alcohol_limit.svg",
    caption: {
      en: "Norway's limit is 0.2 g/L — among the strictest in Europe.",
      so: "Xadka Norway waa 0.2 g/L — mid ka mid ah kuwa ugu adag Yurub.",
      ar: "الحد في النرويج 0.2 جم/لتر — من أشد الحدود صرامة في أوروبا.",
    },
  },
  "rs-2": {
    src: "/illustrations/accident_steps.svg",
    caption: {
      en: "At any accident: SECURE the scene, CARE for the injured, CALL 113.",
      so: "Marka shil dhaco: SUGNAYSO goobta, DAAWO dhaawacyada, KU WAC 113.",
      ar: "عند أي حادث: أمّن الموقع، اعتنِ بالمصابين، اتصل بـ 113.",
    },
  },
  "rs-3": {
    src: "/illustrations/snow_on_car.svg",
    caption: {
      en: "Snow and ice must be cleared from ALL windows, lights and the roof before driving.",
      so: "Baraf iyo aysh waa in laga nadiifiyo dhammaan daaqadaha, nalalka iyo saqafka ka hor inta aanad wadin.",
      ar: "يجب إزالة الثلج والجليد من جميع النوافذ والأضواء والسقف قبل القيادة.",
    },
  },
  "ve-1": {
    src: "/illustrations/tyres_compare.svg",
    caption: {
      en: "Summer tyre vs winter tyre — different tread, different minimum depth, different season.",
      so: "Taayirka xagaaga vs jiilaalka — qaab kala duwan, qoto kala duwan, xilli kala duwan.",
      ar: "إطار الصيف مقابل إطار الشتاء — نمط مختلف، عمق أدنى مختلف، موسم مختلف.",
    },
  },
  "ve-2": {
    src: "/illustrations/lights_always_on.svg",
    caption: {
      en: "Dipped headlights are mandatory 24/7 in Norway — even on the brightest summer day.",
      so: "Nalalka madow waa qasab 24/7 Norway — xataa maalinta xagaaga ugu iftiinta badan.",
      ar: "إضاءة المصابيح الخافتة إلزامية 24/7 في النرويج — حتى في أكثر أيام الصيف إشراقاً.",
    },
  },
  "lw-1": {
    src: "/illustrations/careful_driving.svg",
    caption: {
      en: "Careful driving (§3): the posted limit is the MAX in good conditions — slow down for rain, snow, fog or busy streets.",
      so: "Wadis taxaddar leh (§3): xadka la dejiyey waa UGU SARREEYA xaaladaha wanaagsan — ku gaab roob, baraf, ceeryaamo iyo wadooyin mashquul.",
      ar: "القيادة الحذرة (§3): الحد المثبت هو الحد الأقصى في الظروف الجيدة — أبطئ في المطر والثلج والضباب والشوارع المزدحمة.",
    },
  },
  "lw-2": {
    src: "/illustrations/alcohol_limit.svg",
    caption: {
      en: "Sober driving (§21) — no driving under influence. Limit is 0.2 g/L.",
      so: "Wadis sokorad ka madhan (§21) — ha wadin adigoo sakhran. Xadku waa 0.2 g/L.",
      ar: "القيادة بصحوة (§21) — لا قيادة تحت التأثير. الحد 0.2 جم/لتر.",
    },
  },
  "sg-1": {
    src: "/illustrations/sign_groups.svg",
    caption: {
      en: "Sign families: triangle = warning, red circle = forbidden, blue circle = mandatory, diamond = priority.",
      so: "Qoysaska calaamadaha: saddex-xagal = digniin, goob cas = mamnuuc, goob buluug = qasab, dheeman = horisaarid.",
      ar: "عائلات اللافتات: مثلث = تحذير، دائرة حمراء = ممنوع، دائرة زرقاء = إلزامي، معين = أولوية.",
    },
  },
  "sg-2": {
    src: "/illustrations/zipper_merge.svg",
    caption: {
      en: "Fletting (zipper merge) — alternate one car from each lane at the merge point.",
      so: "Fletting (isku-darka sib) — beddel hal baabuur lane kasta meesha isku-darka.",
      ar: "الاندماج بالتناوب (Fletting) — سيارة من كل مسار بالتناوب عند نقطة الاندماج.",
    },
  },
  "gw-1": { component: RightOfWayScenariosPanel },
  "gw-2": { component: RightOfWayScenariosPanel },
  "gw-3": { component: RightOfWayScenariosPanel },
  "gw-4": { component: RightOfWayScenariosPanel },
};

/* Topic listing — 3 representative sign codes per topic (the existing
   strong topic-card hero). Kept lean and meaningful. */
const TOPIC_HERO_SIGNS = {
  speed_positioning: ["362", "366", "364"],
  road_users: ["146", "142", "510"],
  responsibilities: ["210", "212", "330"],
  vehicle: ["334", "156", "132"],
  laws: ["302", "306", "330"],
  signs: ["202", "204", "306", "362"],
  giving_way: ["202", "204", "208"],
};

function LessonVisual({ visual, t }) {
  if (!visual) return null;
  if (visual.component) {
    const Comp = visual.component;
    return (
      <div data-testid="lesson-illustration" className="my-4 overflow-x-auto">
        <div style={{ minWidth: 340 }}>
          <Comp />
        </div>
      </div>
    );
  }
  return (
    <figure data-testid="lesson-illustration" className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 my-3">
      <img
        src={visual.src}
        alt={visual.caption?.en || "Lesson illustration"}
        loading="lazy"
        className="w-full max-h-[340px] object-contain"
      />
      <figcaption className="text-xs text-gray-600 mt-2 text-center italic">
        {visual.caption?.[t.lang] || visual.caption?.en}
      </figcaption>
    </figure>
  );
}

function Section({ icon: Icon, title, accent = "fjord", children, testid }) {
  const tones = {
    fjord:   "bg-fjord/5 border-fjord/20 text-fjord",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  return (
    <div data-testid={testid} className={`rounded-lg border p-4 ${tones[accent]}`}>
      <div className="flex items-center gap-2 mb-2 font-heading text-sm uppercase tracking-wide">
        <Icon className="w-4 h-4"/> {title}
      </div>
      <div className="space-y-3 text-sm text-gray-800">{children}</div>
    </div>
  );
}

function LessonDetail({ detail, thumbs, t }) {
  if (!detail) return null;

  // Cap related signs to 4 so the visual context stays focused (not scary)
  const sigs = (detail.related_signs || []).filter(c => thumbs[c]).slice(0, 4);

  return (
    <div className="space-y-4 mt-2">
      {detail.intro && (
        <p className="text-base text-gray-800 leading-relaxed border-s-4 border-fjord/40 ps-3 italic">
          {detail.intro}
        </p>
      )}

      {sigs.length > 0 && (
        <div
          data-testid="lesson-signs-strip"
          className="bg-gradient-to-br from-fjord/5 to-white border border-fjord/10 rounded-lg p-3"
        >
          <div className="text-[10px] uppercase tracking-wide text-fjord/80 font-semibold mb-2">
            {t("lessonKeySigns")}
          </div>
          <div className="flex gap-2 flex-wrap">
            {sigs.map(code => (
              <div
                key={code}
                data-testid={`lesson-sign-${code}`}
                title={code}
                className="h-16 w-16 shrink-0 bg-white border border-gray-200 rounded-md p-1.5 grid place-items-center shadow-sm"
              >
                <img src={thumbs[code]} alt={`Sign ${code}`} loading="lazy" className="max-h-full max-w-full object-contain" />
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.key_rules?.length > 0 && (
        <Section icon={ScrollText} title={t("lessonKeyRulesLabel")} accent="fjord" testid="lesson-key-rules">
          <ul className="space-y-2">
            {detail.key_rules.map((r, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-fjord mt-0.5">•</span>
                <div>
                  <div className="text-gray-900">{r.rule}</div>
                  {r.reference && (
                    <Badge variant="outline" className="mt-1 text-[10px] bg-white">📖 {r.reference}</Badge>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {detail.real_situations?.length > 0 && (
        <Section icon={MapPin} title={t("lessonSituationsLabel")} accent="emerald" testid="lesson-situations">
          {detail.real_situations.map((s, i) => (
            <div key={i} className="bg-white/60 rounded-md p-3 border border-emerald-100">
              <div className="font-semibold text-emerald-900 text-sm mb-1">{s.title}</div>
              {s.what_happens && (
                <p className="text-xs text-gray-700 mb-1"><span className="font-semibold">{t("lessonWhatHappens")}</span> {s.what_happens}</p>
              )}
              {s.what_to_do && (
                <p className="text-xs text-gray-700"><span className="font-semibold">{t("lessonWhatToDo")}</span> {s.what_to_do}</p>
              )}
            </div>
          ))}
        </Section>
      )}

      {detail.examples?.length > 0 && (
        <Section icon={Sparkles} title={t("lessonExamplesLabel")} accent="fjord" testid="lesson-examples">
          {detail.examples.map((e, i) => (
            <div key={i} className="bg-white/60 rounded-md p-3 border border-fjord/10">
              <p className="text-sm text-gray-900 mb-1"><span className="font-semibold">{t("lessonSituation")}</span> {e.situation}</p>
              <p className="text-sm text-fjord mb-1">→ {e.correct_action}</p>
              {e.why && <p className="text-xs text-gray-600 italic">{t("lessonWhy")} {e.why}</p>}
            </div>
          ))}
        </Section>
      )}

      {detail.common_mistakes?.length > 0 && (
        <Section icon={AlertTriangle} title={t("lessonMistakesLabel")} accent="amber" testid="lesson-mistakes">
          {detail.common_mistakes.map((m, i) => (
            <div key={i} className="text-sm">
              <p className="text-amber-900"><span className="font-semibold">{t("lessonMistake")}</span> {m.mistake}</p>
              <p className="text-amber-800 mt-0.5"><span className="font-semibold">{t("lessonCorrection")}</span> {m.correction}</p>
            </div>
          ))}
        </Section>
      )}

      {detail.references?.length > 0 && (
        <div className="pt-2 border-t border-gray-100">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">{t("lessonReferencesLabel")}</div>
          <div className="flex flex-wrap gap-1.5">
            {detail.references.map((r, i) => (
              <Badge key={i} variant="outline" className="text-[10px] bg-white">📖 {r}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Top-of-page authority intro — explains why ONLY 7 topics, with
   official Statens vegvesen references so learners feel the
   curriculum is complete. */
function CurriculumIntro({ t }) {
  return (
    <Card data-testid="curriculum-intro" className="p-5 sm:p-6 mb-8 bg-gradient-to-br from-fjord to-fjord-700 text-white border-0 overflow-hidden relative">
      <div className="absolute -end-12 -top-12 w-48 h-48 rounded-full bg-white/5"></div>
      <div className="flex items-start gap-3 mb-3 relative">
        <div className="w-10 h-10 rounded-md bg-white/15 grid place-items-center shrink-0">
          <ShieldCheck className="w-5 h-5"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold mb-1">
            {t("introBadge")}
          </div>
          <h2 className="font-heading text-xl sm:text-2xl font-semibold">{t("introTitle")}</h2>
        </div>
      </div>
      <p className="text-sm text-white/90 leading-relaxed mb-4 max-w-3xl relative">{t("introBody")}</p>
      <div className="flex flex-wrap gap-2 relative">
        <a href="https://www.vegvesen.no/forerkort/ta-forerkort/" target="_blank" rel="noreferrer"
          data-testid="intro-ref-vegvesen"
          className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-md px-3 py-1.5 text-xs font-medium transition-colors">
          {t("introRefVegvesen")} <ExternalLink className="w-3 h-3"/>
        </a>
        <a href="https://lovdata.no/dokument/SF/forskrift/2004-10-01-1339" target="_blank" rel="noreferrer"
          data-testid="intro-ref-laereplan"
          className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-md px-3 py-1.5 text-xs font-medium transition-colors">
          {t("introRefLaereplan")} <ExternalLink className="w-3 h-3"/>
        </a>
        <a href="https://lovdata.no/dokument/NL/lov/1965-06-18-4" target="_blank" rel="noreferrer"
          data-testid="intro-ref-vegtraf"
          className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-md px-3 py-1.5 text-xs font-medium transition-colors">
          {t("introRefVegtraf")} <ExternalLink className="w-3 h-3"/>
        </a>
      </div>
    </Card>
  );
}

export default function Learn() {
  const { topicId } = useParams();
  const { t, lang } = useLang();
  const tWithLang = useMemo(() => Object.assign((key) => t(key), { lang }), [t, lang]);
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState(null);
  const [thumbs, setThumbs] = useState({});
  const [listThumbs, setListThumbs] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (topicId) return;
    const codes = Object.values(TOPIC_HERO_SIGNS).flat();
    api.get(`/signs/thumbs?codes=${codes.join(",")}`)
      .then(r => setListThumbs(r.data || {}))
      .catch(() => setListThumbs({}));
  }, [topicId]);

  useEffect(() => {
    if (topicId) {
      api.get(`/topics/${topicId}`, { params: { lang } }).then(r => {
        setTopic(r.data);
        const codes = new Set();
        for (const l of r.data.lessons || []) {
          for (const c of l.detail?.related_signs || []) codes.add(c);
        }
        if (codes.size === 0) { setThumbs({}); return; }
        api.get(`/signs/thumbs?codes=${[...codes].join(",")}`)
          .then(rr => setThumbs(rr.data || {}))
          .catch(() => setThumbs({}));
      });
    } else {
      setTopic(null);
      api.get("/topics", { params: { lang } }).then(r => setTopics(r.data));
    }
  }, [topicId, lang]);

  if (topicId && topic) {
    return (
      <div className="min-h-screen bg-gray-50 relative">
        <Navbar/>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <button data-testid="back-to-topics" onClick={()=>navigate("/learn")}
            className="text-sm text-fjord hover:underline mb-4 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180"/> {t("backToTopics")}
          </button>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">{topic.title}</h1>
          <p className="text-gray-600 mb-6">{topic.description}</p>

          <Accordion type="single" collapsible className="space-y-3" defaultValue={topic.lessons?.[0]?.id}>
            {topic.lessons.map(l => {
              const visual = LESSON_VISUALS[l.id];
              return (
                <AccordionItem key={l.id} value={l.id} className="bg-white border border-gray-200 rounded-lg px-5">
                  <AccordionTrigger data-testid={`lesson-${l.id}`} className="font-heading text-base font-semibold hover:no-underline">
                    <span className="flex items-center gap-3 text-start">
                      {visual && (
                        <span className="h-10 w-10 shrink-0 bg-white border border-gray-200 rounded-md p-1 grid place-items-center">
                          <img src={visual.src} alt="" className="max-h-full max-w-full object-contain" />
                        </span>
                      )}
                      <span>{l.title}</span>
                      {l.detail && (
                        <Badge variant="outline" className="ms-2 text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700">
                          {t("lessonFullDetailBadge")}
                        </Badge>
                      )}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed pt-1 pb-4">
                    <p>{l.content}</p>
                    <LessonVisual visual={visual} t={tWithLang} />
                    <LessonDetail detail={l.detail} thumbs={thumbs} t={t} />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <div className="mt-8">
            <Button onClick={()=>navigate(`/practice?topic=${topic.topic_id}`)} data-testid="learn-practice-cta" className="bg-fjord hover:bg-fjord-700">
              <Target className="w-4 h-4 me-2"/> {t("practiceTopic")}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <Navbar/>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 data-testid="learn-title" className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">{t("learnTitle")}</h1>
        <p className="text-gray-600 mb-8">{t("learnSubtitle")}</p>

        <CurriculumIntro t={t} />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {topics.map(tp => {
            const previewCodes = (TOPIC_HERO_SIGNS[tp.topic_id] || []).filter(c => listThumbs[c]).slice(0, 3);
            const photo = TOPIC_PHOTOS[tp.topic_id];
            return (
              <Card key={tp.topic_id} data-testid={`topic-${tp.topic_id}`}
                onClick={()=>navigate(`/learn/${tp.topic_id}`)}
                className="overflow-hidden cursor-pointer border border-gray-200 bg-white hover:border-fjord/40 hover:shadow-md transition-all group">
                {/* Photo hero — real Norwegian road photo as context */}
                <div className="relative h-36 border-b border-gray-100 overflow-hidden">
                  {photo ? (
                    <>
                      <img
                        src={photo.url}
                        alt={tp.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {/* dark gradient so sign chips stay readable */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-fjord/10 to-fjord/20" />
                  )}
                  {/* sign chips pinned to bottom-left */}
                  {previewCodes.length > 0 && (
                    <div className="absolute bottom-3 left-3 flex gap-1.5">
                      {previewCodes.map(c => (
                        <div key={c} data-testid={`topic-thumb-${tp.topic_id}-${c}`}
                          className="h-10 w-10 bg-white/95 border border-white/60 rounded-md p-1 grid place-items-center shadow">
                          <img src={listThumbs[c]} alt={`Sign ${c}`} loading="lazy" className="max-h-full max-w-full object-contain" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold mb-1">{tp.title}</h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{tp.description}</p>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    {tp.lesson_count} {t("lessons")} <ChevronRight className="w-3.5 h-3.5 rtl:rotate-180"/>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}
